import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { useBlockUserMutation } from "Api/Mutations/Social/useBlockUserMutation";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { ContentReportDialog } from "Components/Support/ContentReport/ContentReportDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";

const RESOURCES = Resources.Social.PublicProfile;

type Props = {
  identityUserPublicID: string;
  handle: string | null | undefined;
  displayName: string | null | undefined;
  isBlockVisible: boolean;
  isDisabled?: boolean;
  /**
   * The profile-page host intercepts a handle-less caller with the HandleGate before
   * the block confirmation; list hosts omit it (their rows imply the caller already
   * holds a handle — the graph can't be built without one).
   */
  needsHandle?: boolean;
  onHandleGate?: () => void;
};

/**
 * The kebab (⋮) menu with the rare/destructive social actions (ZS-1320 Phase 2),
 * shared by the public profile and the mutual/request list rows. Type-blind — hosts
 * map their own DTOs onto the primitive props. Block asks for confirmation and runs
 * its own mutation; Report opens the DSA notice form (ZS-1743) for this profile.
 */
export const SocialUserActionsMenu = ({
  identityUserPublicID,
  handle,
  displayName,
  isBlockVisible,
  isDisabled = false,
  needsHandle = false,
  onHandleGate,
}: Props) => {
  const { t } = useResource();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  // Remounts the report dialog on every open so each visit starts from a blank form —
  // resetting on close would snap the closing dialog back to the form while it fades out.
  const [reportInstanceKey, setReportInstanceKey] = useState(0);
  const {
    isOpen: isConfirmOpen,
    open: openConfirm,
    handleResult,
  } = useConfirmDialog();
  const { mutate: blockUser, isPending } = useBlockUserMutation();

  const closeMenu = () => setAnchorElement(null);

  const handleBlockClick = () => {
    closeMenu();

    if (needsHandle) {
      onHandleGate?.();
      return;
    }

    openConfirm();
  };

  const handleReportClick = () => {
    closeMenu();
    setReportInstanceKey((key) => key + 1);
    setIsReportOpen(true);
  };

  return (
    <>
      <IconButton
        aria-label={t(RESOURCES.MoreActions)}
        size="small"
        onClick={(e) => setAnchorElement(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorElement} open={!!anchorElement} onClose={closeMenu}>
        {isBlockVisible && (
          <MenuItem
            disabled={isDisabled || isPending}
            onClick={handleBlockClick}
          >
            {t(RESOURCES.Block)}
          </MenuItem>
        )}
        <MenuItem onClick={handleReportClick}>{t(RESOURCES.Report)}</MenuItem>
      </Menu>
      <ConfirmDialog
        isDestructive
        isOpen={isConfirmOpen}
        modalTitle={t(RESOURCES.BlockConfirmTitle)}
        modalContent={
          <Typography>{t(RESOURCES.BlockConfirmDescription)}</Typography>
        }
        confirmButtonText={t(RESOURCES.Block)}
        handleResult={handleResult(() => blockUser(identityUserPublicID))}
      />
      {/* Mounted only once used: this menu sits on every row of the People lists, and a closed
          dialog per row would still run its form-guard hooks for nothing. */}
      {reportInstanceKey > 0 && (
        <ContentReportDialog
          key={reportInstanceKey}
          isOpen={isReportOpen}
          target={{
            kind: "profile",
            identityUserPublicID,
            handle,
            displayName,
          }}
          onClose={() => setIsReportOpen(false)}
        />
      )}
    </>
  );
};
